// Sanskrit Premium (/sanskrit): the notice shown instead of a Premium part of the Sanskrit course, and the gate that
// picks between the two. Access comes from the shared session (access.sanskrit: staff, a Sanskrit or Sanskrit + Prakrit
// plan). The Sanskrit course runs in the browser, so this gate is what keeps it closed.

import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Check, Crown, Hourglass, LoaderCircle, RefreshCw, Sparkles, WifiOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/client/session'
import { PLAN_BY_ID } from '@/lib/plans'

export const SANSKRIT_FEATURES: string[] = [
    'All 22 lessons with the teacher board and every exercise',
    'The whole Grammar Lab: every lakāra, pada, case, sandhi and compound',
    'Every story and dialogue, read aloud word by word',
    'Every practice game, the sentence playground and speaking practice',
    'Early access to new lessons as they are checked'
]

const BUTTON = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-6 py-2.5 font-semibold transition-colors disabled:opacity-60'

interface SanskritNoticeProps {
    /** What is locked, for the heading: "This lesson", "The Lakāra page" … */
    what?: string
    compact?: boolean
    className?: string
}

export const SanskritPremiumNotice: FC<SanskritNoticeProps> = ({ what = 'This part of the course', compact = false, className }): ReactNode => {
    const router = useRouter()
    const { user, access, loading, unavailable, refresh } = useSession()
    const pending = access.pendingRequest
    const Heading = compact ? 'h2' : 'h1'

    // The account could not be checked: never ask a member to buy what they may already have.
    if (unavailable || access.unavailable) {
        return (
            <section
                aria-labelledby='sanskrit-unavailable-title'
                className={cn(
                    'mx-auto flex w-full flex-col items-center gap-5 text-center',
                    compact ? 'max-w-2xl rounded-[2rem] border border-saffron/40 bg-saffron-soft/50 px-5 py-8' : 'max-w-xl px-4 py-14 sm:py-20',
                    className
                )}
            >
                <span className='flex size-16 items-center justify-center rounded-full bg-saffron/20 ring-1 ring-saffron/40' aria-hidden='true'>
                    <WifiOff className='size-8 text-saffron-strong' />
                </span>
                <Heading id='sanskrit-unavailable-title' className={cn('font-heading text-foreground', compact ? 'text-2xl' : 'text-3xl')}>
                    We cannot check your account right now
                </Heading>
                <p className='max-w-md text-[0.9375rem] text-muted-foreground'>
                    If you have Sanskrit Premium, it is safe and nothing has changed. Please try again in a moment.
                </p>
                <button
                    type='button'
                    onClick={() => {
                        void refresh()
                        router.replace(router.asPath)
                    }}
                    className={cn(BUTTON, 'bg-saffron text-ink shadow-md hover:brightness-105')}
                >
                    <RefreshCw className='size-4' aria-hidden='true' />
                    Try again
                </button>
            </section>
        )
    }

    return (
        <section
            aria-labelledby='sanskrit-premium-title'
            className={cn(
                'mx-auto flex w-full flex-col items-center gap-5 text-center',
                compact ? 'max-w-2xl rounded-[2rem] border border-saffron/40 bg-saffron-soft/50 px-5 py-8' : 'max-w-xl px-4 py-14 sm:py-20',
                className
            )}
        >
            <span className='flex size-16 items-center justify-center rounded-full bg-saffron/20 ring-1 ring-saffron/40' aria-hidden='true'>
                <Crown className='size-8 text-saffron-strong' />
            </span>
            <p className='text-sm font-semibold uppercase tracking-[0.14em] text-saffron-strong'>Sanskrit Premium</p>
            <Heading id='sanskrit-premium-title' className={cn('font-heading text-foreground', compact ? 'text-2xl' : 'text-3xl')}>
                {what} is part of Sanskrit Premium
            </Heading>

            <ul className='flex w-full max-w-md flex-col gap-2 text-left'>
                {SANSKRIT_FEATURES.map(f => (
                    <li key={f} className='flex items-start gap-2.5 text-[0.9375rem]'>
                        <Check className='mt-0.5 size-5 shrink-0 text-tulsi' aria-hidden='true' />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            {loading ? (
                <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-label='Loading' />
            ) : (
                <div className='flex w-full flex-col items-center gap-3'>
                    {!user && (
                        <>
                            <Link href={`/account?tab=signup&next=${encodeURIComponent(router.asPath)}`} className={cn(BUTTON, 'bg-saffron text-ink shadow-md hover:brightness-105')}>
                                <Sparkles className='size-4' aria-hidden='true' />
                                Create a free account
                            </Link>
                            <p className='text-sm text-muted-foreground'>
                                Already have an account?{' '}
                                <Link href={`/account?next=${encodeURIComponent(router.asPath)}`} className='font-semibold text-saffron-strong underline underline-offset-4'>Sign in</Link>
                            </p>
                        </>
                    )}
                    {user && pending && (
                        <p className='flex items-start gap-2 rounded-2xl bg-muted px-4 py-3 text-left text-sm'>
                            <Hourglass className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                            We have your request for {PLAN_BY_ID[pending.plan].name.en}. We will contact you with the payment details.
                        </p>
                    )}
                    <Link
                        href='/pricing?plan=sanskrit&period=month#request'
                        className={cn(BUTTON, user && !pending ? 'bg-saffron text-ink shadow-md hover:brightness-105' : 'border-2 border-ink text-ink hover:bg-ink-soft')}
                    >
                        <Crown className='size-4' aria-hidden='true' />
                        Get Sanskrit Premium
                    </Link>
                </div>
            )}
        </section>
    )
}

/** Shows its children with Sanskrit Premium and the Sanskrit Premium notice otherwise (a quiet placeholder while loading). */
export const SanskritGate: FC<SanskritNoticeProps & { children: ReactNode, free?: boolean }> = ({ children, free = false, ...notice }): ReactNode => {
    const { access, loading } = useSession()

    if (free) return children

    if (loading) {
        return (
            <div role='status' className='flex min-h-[40vh] items-center justify-center'>
                <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-hidden='true' />
                <span className='sr-only'>Loading</span>
            </div>
        )
    }

    return access.sanskrit ? children : <SanskritPremiumNotice {...notice} />
}

/** A small crown chip for a Sanskrit Premium item, shown only to visitors without Sanskrit Premium. */
export const SanskritPremiumMark: FC<{ className?: string }> = ({ className }): ReactNode => {
    const { access, loading, unavailable } = useSession()

    if (loading || unavailable || access.sanskrit) return null

    return (
        <span className={cn('inline-flex items-center gap-1 rounded-full bg-saffron-soft px-2.5 py-0.5 text-xs font-semibold text-saffron-strong', className)}>
            <Crown className='size-3.5' aria-hidden='true' />
            Premium
        </span>
    )
}
